import { GROUP_BADGE } from "../types/public";
import { HOME_QUICK_GROUP_FILTERS, HOME_QUICK_PERSON_LABELS } from "../lib/home-quick-search";

const PERSON_SET = new Set<string>(HOME_QUICK_PERSON_LABELS);
const GROUP_CODE_SET = new Set([
  ...HOME_QUICK_GROUP_FILTERS.map((g) => g.groupCode),
  "other",
]);

export type AdminPerformersSelection = {
  persons: string[];
  groupCodes: string[];
};

export function emptyAdminPerformersSelection(): AdminPerformersSelection {
  return { persons: [], groupCodes: [] };
}

const GROUP_CHIPS = [
  ...HOME_QUICK_GROUP_FILTERS,
  { label: GROUP_BADGE.other.label, groupCode: "other" as const },
];

type Props = {
  value: AdminPerformersSelection;
  onChange: (next: AdminPerformersSelection) => void;
  disabled?: boolean;
};

function splitNames(text: string): string[] {
  return text
    .split(/[,、\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function mergeUniquePersons(...lists: string[][]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const list of lists) {
    for (const n of list) {
      if (seen.has(n)) continue;
      seen.add(n);
      out.push(n);
    }
  }
  return out;
}

function togglePerson(value: AdminPerformersSelection, name: string): AdminPerformersSelection {
  const has = value.persons.includes(name);
  return {
    ...value,
    persons: has ? value.persons.filter((p) => p !== name) : [...value.persons, name],
  };
}

function toggleGroup(value: AdminPerformersSelection, groupCode: string): AdminPerformersSelection {
  const has = value.groupCodes.includes(groupCode);
  return {
    ...value,
    groupCodes: has ? value.groupCodes.filter((c) => c !== groupCode) : [...value.groupCodes, groupCode],
  };
}

export function adminPerformersSelectionFromRow(
  performersJson: unknown,
  groupCode: unknown,
  parseList: (v: unknown) => string[],
): AdminPerformersSelection {
  const persons = parseList(performersJson);
  const gc = String(groupCode ?? "").trim();
  const groupCodes = gc && GROUP_CODE_SET.has(gc) ? [gc] : [];
  return { persons, groupCodes };
}

export function adminPerformersSelectionSummary(sel: AdminPerformersSelection): string {
  const groupLabels = sel.groupCodes.map((c) => GROUP_CHIPS.find((g) => g.groupCode === c)?.label ?? c);
  return [...sel.persons, ...groupLabels].join("、");
}

export function AdminPerformersPicker({ value, onChange, disabled }: Props) {
  const fromChips = value.persons.filter((n) => PERSON_SET.has(n));
  const extras = value.persons.filter((n) => !PERSON_SET.has(n));
  const extrasText = extras.join("、");

  const onExtrasChange = (text: string) => {
    onChange({
      ...value,
      persons: mergeUniquePersons(fromChips, splitNames(text)),
    });
  };

  const summary = adminPerformersSelectionSummary(value);

  return (
    <fieldset className="home-visitor-picker admin-performers-picker" disabled={disabled}>
      <legend className="home-visitor-picker-legend">出演者・グループ</legend>
      <p className="home-visitor-picker-hint">該当する名前やグループをタップして選んでください。複数選べます。</p>
      <div className="home-chip-label">出演者</div>
      <div className="row home-chips home-visitor-picker-chips">
        {HOME_QUICK_PERSON_LABELS.map((name) => {
          const active = value.persons.includes(name);
          return (
            <button
              key={name}
              type="button"
              className={`chip${active ? " active" : ""}`}
              aria-pressed={active}
              onClick={() => onChange(togglePerson(value, name))}
            >
              {name}
            </button>
          );
        })}
      </div>
      <div className="home-chip-label" style={{ marginTop: 8 }}>
        グループ
      </div>
      <div className="row home-chips home-visitor-picker-chips">
        {GROUP_CHIPS.map((g) => {
          const active = value.groupCodes.includes(g.groupCode);
          return (
            <button
              key={g.groupCode}
              type="button"
              className={`chip${active ? " active" : ""}`}
              aria-pressed={active}
              onClick={() => onChange(toggleGroup(value, g.groupCode))}
            >
              {g.label}
            </button>
          );
        })}
      </div>
      <label style={{ display: "block", marginTop: 10 }}>
        <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 4 }}>リストにない出演者（カンマ区切り・任意）</div>
        <input
          className="input"
          type="text"
          placeholder="例: 米村姫良々"
          value={extrasText}
          onChange={(e) => onExtrasChange(e.target.value)}
        />
      </label>
      {summary ? (
        <p className="home-visitor-picker-selected mono" aria-live="polite">
          選択中: {summary}
        </p>
      ) : null}
    </fieldset>
  );
}
